import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, ArrowRight, ArrowLeft, Clock, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { CATEGORIES, SERVICES, SPECIALISTS, BRANCHES, formatPrice, getAvailableSlots, getOpeningHoursForDate, formatTime } from "@/data/salon";
import { createAppointment, createCheckout } from "@/lib/api";
import { useBooking } from "@/context/BookingContext";

const emptyDetails = { name: "", email: "", phone: "", notes: "" };

const nextDays = (count) => {
  const out = [];
  const d = new Date();
  for (let i = 1; out.length < count; i++) {
    const day = new Date(d);
    day.setDate(d.getDate() + i);
    out.push(day);
  }
  return out;
};

export const BookingModal = () => {
  const { open, setOpen, preselect } = useBooking();
  const [step, setStep] = useState(0);
  const [category, setCategory] = useState("brows");
  const [serviceId, setServiceId] = useState(null);
  const [specialist, setSpecialist] = useState(SPECIALISTS[0]);
  const [branch, setBranch] = useState(BRANCHES[0].id);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [details, setDetails] = useState(emptyDetails);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onEscape);
    return () => window.removeEventListener("keydown", onEscape);
  }, [open, setOpen]);

  const days = useMemo(() => nextDays(6), []);
  const service = SERVICES.find((s) => s.id === serviceId);
  const branchObj = BRANCHES.find((b) => b.id === branch);
  const catRestrict = category === "hair" ? ["springburn", "maryhill"] : null;
  const allowedBranchIds = catRestrict || service?.branches || BRANCHES.map((b) => b.id);
  const visibleBranches = BRANCHES.filter((b) => allowedBranchIds.includes(b.id));
  const catServices = SERVICES.filter((s) => s.category === category && (!s.branches || s.branches.includes(branch)));
  const availableSlots = useMemo(
    () => getAvailableSlots(branch, date, service?.duration),
    [branch, date, service]
  );
  const selectedDayHours = date ? getOpeningHoursForDate(branch, date) : null;

  useEffect(() => {
    if (time && date && !availableSlots.includes(time)) {
      setTime(null);
    }
  }, [availableSlots, date, time]);

  useEffect(() => {
    const allowed = service?.branches || (category === "hair" ? ["springburn", "maryhill"] : null);
    if (allowed && !allowed.includes(branch)) {
      setBranch(allowed[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, category]);

  useEffect(() => {
    if (service?.branches && !service.branches.includes(branch)) {
      setServiceId(null);
    }
  }, [branch, service]);

  useEffect(() => {
    if (open) {
      setStep(0); setDone(false); setDate(null); setTime(null);
      setSpecialist(SPECIALISTS[0]); setDetails(emptyDetails);
      if (preselect) {
        const s = SERVICES.find((x) => x.id === preselect);
        if (s) {
          setCategory(s.category);
          setServiceId(s.id);
          setBranch(s.branches?.[0] || BRANCHES[0].id);
        }
      } else {
        setServiceId(null);
        setBranch(BRANCHES[0].id);
      }
    }
  }, [open, preselect]);

  const canNext =
    (step === 0 && serviceId) ||
    (step === 1 && date && time && availableSlots.includes(time)) ||
    step === 2;

  const submit = async () => {
    if (!details.name || !details.email || !details.phone) {
      toast.error("Please complete your details.");
      return;
    }
    setSubmitting(true);
    try {
      await createAppointment({
        ...details,
        service_id: service.id,
        service_name: service.name,
        specialist,
        branch_id: branch,
        branch_name: branchObj.name,
        price: service.price,
        from_price: !!service.from,
        date: date.toDateString(),
        time,
      });
      setDone(true);
      toast.success("Your appointment is confirmed.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const steps = ["Service", "Date & Time", "Your Details"];

  const payOnline = async () => {
    if (!details.name || !details.email || !details.phone) {
      toast.error("Please complete your details.");
      return;
    }
    setSubmitting(true);
    try {
      const { url } = await createCheckout({
        ...details,
        service_id: service.id,
        service_name: service.name,
        specialist,
        branch_id: branch,
        branch_name: branchObj.name,
        from_price: !!service.from,
        date: date.toDateString(),
        time,
        origin_url: window.location.origin,
      });
      window.location.href = url;
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not start payment. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Could not start payment. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-ink/60 backdrop-blur-md p-0 sm:p-6"
          onClick={() => setOpen(false)}
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
            transition={{ ease: [0.16, 1, 0.3, 1], duration: 0.5 }}
            onClick={(e) => e.stopPropagation()}
            data-testid="booking-modal"
            className="relative w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-cream border border-pink/30"
          >
            <button
              onClick={() => setOpen(false)}
              data-testid="booking-close-btn"
              className="absolute top-5 right-5 z-10 rounded-full bg-ink/5 p-2 hover:bg-ink/10"
            >
              <X className="h-5 w-5 text-ink" />
            </button>

            {done ? (
              <div data-testid="booking-confirmation-alert" className="px-8 py-16 text-center">
                <motion.div
                  initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", delay: 0.1 }}
                  className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-pink"
                >
                  <Check className="h-8 w-8 text-ink" />
                </motion.div>
                <h3 className="font-serif text-3xl text-ink">You're booked.</h3>
                <p className="mt-3 text-muted font-light max-w-md mx-auto">
                  {service?.name} at Fabrowz {branchObj?.name} with {specialist} on {date?.toDateString()} at {time}. A confirmation has been noted under {details.email}.
                </p>
                <button
                  onClick={() => setOpen(false)}
                  className="mt-8 rounded-full bg-ink px-8 py-3.5 text-xs uppercase tracking-[0.2em] text-cream hover:bg-inkhover"
                >
                  Wonderful
                </button>
              </div>
            ) : (
              <div className="p-6 sm:p-10">
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-pink">Reserve · Fabrowz</p>
                <h3 className="mt-2 font-serif text-3xl text-ink">Book your ritual</h3>

                {/* stepper */}
                <div className="mt-6 flex items-center gap-2">
                  {steps.map((s, i) => (
                    <div key={s} className="flex items-center gap-2 flex-1">
                      <div className={`flex h-7 w-7 items-center justify-center rounded-full font-mono text-xs ${i <= step ? "bg-ink text-cream" : "bg-ink/10 text-ink/50"}`}>{i + 1}</div>
                      <span className={`hidden sm:block font-mono text-[10px] uppercase tracking-[0.15em] ${i <= step ? "text-ink" : "text-ink/40"}`}>{s}</span>
                      {i < 2 && <div className={`h-px flex-1 ${i < step ? "bg-ink" : "bg-ink/15"}`} />}
                    </div>
                  ))}
                </div>

                <div className="mt-8 min-h-[300px]">
                  <AnimatePresence mode="wait">
                    {/* STEP 0 */}
                    {step === 0 && (
                      <motion.div key="s0" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} data-testid="booking-form-step-service">
                        <div className="mb-5">
                          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted mb-2">Choose your branch</p>
                          <div className="grid sm:grid-cols-3 gap-2">
                            {visibleBranches.map((b) => (
                              <button
                                key={b.id}
                                onClick={() => setBranch(b.id)}
                                data-testid={`booking-branch-${b.id}`}
                                className={`text-left rounded-xl border p-3 transition-all ${branch === b.id ? "border-pink bg-blush" : "border-pink/20 hover:border-pink/50"}`}
                              >
                                <p className="font-serif text-base text-ink leading-tight">{b.name}</p>
                                <p className="font-mono text-[9px] text-muted mt-1 leading-snug">{b.address}</p>
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-5">
                          {CATEGORIES.map((c) => (
                            <button
                              key={c.id}
                              onClick={() => { setCategory(c.id); setServiceId(null); }}
                              data-testid={`booking-category-${c.id}`}
                              className={`rounded-full px-4 py-2 font-mono text-[10px] uppercase tracking-[0.15em] border transition-all ${category === c.id ? "bg-ink text-cream border-ink" : "text-ink/60 border-pink/30 hover:border-pink"}`}
                            >
                              {c.label}
                            </button>
                          ))}
                        </div>
                        <div className="space-y-2">
                          {catServices.map((s) => (
                            <button
                              key={s.id}
                              onClick={() => setServiceId(s.id)}
                              data-testid={`booking-service-${s.id}`}
                              className={`w-full text-left rounded-xl border p-4 transition-all ${serviceId === s.id ? "border-pink bg-blush" : "border-pink/20 hover:border-pink/50"}`}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-serif text-lg text-ink">{s.name}</p>
                                  <p className="font-mono text-[11px] text-muted flex items-center gap-1 mt-1"><Clock className="h-3 w-3" />{s.duration}</p>
                                </div>
                                <span className="font-serif text-xl text-pink">{s.from && <span className="text-[10px] text-muted mr-1 align-middle">from</span>}£{formatPrice(s.price)}</span>
                              </div>
                            </button>
                          ))}
                        </div>
                        <div className="mt-5">
                          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted mb-2">Specialist</p>
                          <div className="flex flex-wrap gap-2">
                            {SPECIALISTS.map((sp) => (
                              <button key={sp} onClick={() => setSpecialist(sp)}
                                className={`rounded-full px-4 py-2 text-xs border transition-all ${specialist === sp ? "bg-ink text-cream border-ink" : "text-ink/70 border-pink/30 hover:border-pink"}`}>
                                {sp}
                              </button>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* STEP 1 */}
                    {step === 1 && (
                      <motion.div key="s1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} data-testid="booking-form-step-date">
                        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted mb-3">Select a date</p>
                        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-8">
                          {days.map((d) => {
                            const sel = date && d.toDateString() === date.toDateString();
                            return (
                              <button key={d.toISOString()} onClick={() => setDate(d)}
                                data-testid={`booking-date-${d.toISOString().slice(0, 10)}`}
                                className={`rounded-xl border py-3 transition-all ${sel ? "bg-ink text-cream border-ink" : "border-pink/25 hover:border-pink text-ink"}`}>
                                <p className="font-mono text-[10px] uppercase">{d.toLocaleDateString("en", { weekday: "short" })}</p>
                                <p className="font-serif text-xl">{d.getDate()}</p>
                                <p className="font-mono text-[9px] uppercase">{d.toLocaleDateString("en", { month: "short" })}</p>
                              </button>
                            );
                          })}
                        </div>
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted">Select a time</p>
                          {selectedDayHours && (
                            <p data-testid="booking-opening-hours-note" className="font-mono text-[10px] uppercase tracking-[0.14em] text-pink">
                              Open {formatTime(selectedDayHours[0])}–{formatTime(selectedDayHours[1])}
                            </p>
                          )}
                        </div>
                        {!date ? (
                          <p data-testid="booking-time-hint" className="rounded-xl border border-pink/20 bg-blush px-4 py-3 text-sm text-muted">
                            Choose a date to see available times.
                          </p>
                        ) : availableSlots.length === 0 ? (
                          <p data-testid="booking-no-times-message" className="rounded-xl border border-pink/20 bg-blush px-4 py-3 text-sm text-muted">
                            No times are available for this treatment on the selected date. Please choose another day.
                          </p>
                        ) : (
                          <div className="grid grid-cols-4 gap-2" data-testid="booking-time-slots">
                            {availableSlots.map((t) => (
                              <button key={t} onClick={() => setTime(t)}
                                data-testid={`booking-time-${t.replace(":", "")}`}
                                className={`rounded-lg border py-2.5 font-mono text-sm transition-all ${time === t ? "bg-pink text-ink border-pink" : "border-pink/25 hover:border-pink text-ink"}`}>
                                {t}
                              </button>
                            ))}
                          </div>
                        )}
                      </motion.div>
                    )}

                    {/* STEP 2 */}
                    {step === 2 && (
                      <motion.div key="s2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} data-testid="booking-form-step-details" className="space-y-4">
                        <div className="rounded-xl bg-blush border border-pink/20 p-4 flex items-center justify-between">
                          <div>
                            <p className="font-serif text-lg text-ink">{service?.name}</p>
                            <p className="font-mono text-[11px] text-muted">{branchObj?.name} · {specialist}</p>
                            <p className="font-mono text-[11px] text-muted">{date?.toDateString()} · {time}</p>
                          </div>
                          <span className="font-serif text-2xl text-pink">{service?.from && <span className="text-xs text-muted mr-1 align-middle">from</span>}£{formatPrice(service?.price || 0)}</span>
                        </div>
                        {[
                          { k: "name", label: "Full name", type: "text", tid: "booking-input-name" },
                          { k: "email", label: "Email", type: "email", tid: "booking-input-email" },
                          { k: "phone", label: "Phone", type: "tel", tid: "booking-input-phone" },
                        ].map((f) => (
                          <input key={f.k} type={f.type} placeholder={f.label} data-testid={f.tid}
                            value={details[f.k]} onChange={(e) => setDetails({ ...details, [f.k]: e.target.value })}
                            className="w-full rounded-xl border border-pink/25 bg-surface px-4 py-3 text-sm text-ink placeholder:text-muted/60 focus:border-pink outline-none" />
                        ))}
                        <textarea placeholder="Notes (allergies, preferences…)" data-testid="booking-input-notes"
                          value={details.notes} onChange={(e) => setDetails({ ...details, notes: e.target.value })} rows={3}
                          className="w-full rounded-xl border border-pink/25 bg-surface px-4 py-3 text-sm text-ink placeholder:text-muted/60 focus:border-pink outline-none resize-none" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* nav */}
                <div className="mt-6 flex items-center justify-between gap-3">
                  <button
                    onClick={() => (step === 0 ? setOpen(false) : setStep(step - 1))}
                    className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-ink/60 hover:text-ink"
                  >
                    <ArrowLeft className="h-4 w-4" /> {step === 0 ? "Cancel" : "Back"}
                  </button>
                  {step < 2 ? (
                    <button
                      onClick={() => canNext && setStep(step + 1)}
                      disabled={!canNext}
                      data-testid="booking-next-btn"
                      className="flex items-center gap-2 rounded-full bg-ink px-7 py-3 text-xs uppercase tracking-[0.18em] text-cream disabled:opacity-40 hover:bg-inkhover transition-colors"
                    >
                      Continue <ArrowRight className="h-4 w-4" />
                    </button>
                  ) : (
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <button
                        onClick={submit}
                        disabled={submitting}
                        data-testid="booking-form-submit-btn"
                        className="flex items-center gap-2 rounded-full border border-ink px-5 py-3 text-xs uppercase tracking-[0.15em] text-ink disabled:opacity-50 hover:bg-ink hover:text-cream transition-colors"
                      >
                        {submitting ? "…" : "Pay at salon"} <Check className="h-4 w-4" />
                      </button>
                      <button
                        onClick={payOnline}
                        disabled={submitting}
                        data-testid="booking-pay-online-btn"
                        className="flex items-center gap-2 rounded-full bg-pink px-6 py-3 text-xs uppercase tracking-[0.15em] text-cream disabled:opacity-50 hover:bg-ink transition-colors"
                      >
                        <CreditCard className="h-4 w-4" /> Pay online {service?.from ? "from " : ""}£{formatPrice(service?.price || 0)}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
